import * as React from "react";
const { useState, useRef, useEffect } = React;
import { createPortal } from "react-dom";
import { useStore } from "@nanostores/react";
import { Resizable } from "react-resizable";
import "react-resizable/css/styles.css";
import SBProvider from "@sendbird/uikit-react/SendbirdProvider";
import SBConversation from "@sendbird/uikit-react/GroupChannel";
import { useGroupChannelContext } from "@sendbird/uikit-react/GroupChannel/context";
import MessageInputWrapper from "@sendbird/uikit-react/GroupChannel/components/MessageInputWrapper";
import { Message } from "@sendbird/uikit-react/GroupChannel/components/Message";
import SBChannelList from "@sendbird/uikit-react/GroupChannelList";
import "@sendbird/uikit-react/dist/index.css";
import { SBChatShow, SBChatUnread, SBChatChannel, SBChatAssistant } from "../stores/SBChatStore";
import SBJobRelationship from "./SBJobRelationship";
import { AssistantChat } from "../../assistant/components/AssistantChat";
import { AssistantConversationList } from "../../assistant/components/AssistantConversationList";
import { AssistantStyles } from "../../assistant/components/AssistantStyles";
import { assistantWidget } from "../../assistant/stores/assistantStore";

// Quick replies above the composer: one-tap sends for the highest-volume
// messages, per side of the conversation. Renders the default UIKit
// composer underneath so send behavior is unchanged.
// Short chip labels that seed a fuller, ready-to-send draft into the field.
const DD_RECRUITER_CHIPS = [
  {
    label: "Still interested?",
    message: "Hi! Just checking in. Are you still interested in this opportunity? Happy to answer any questions.",
  },
  {
    label: "Quick call?",
    message: "Would you be open to a quick call this week? I can walk you through the role and answer any questions.",
  },
  {
    label: "Availability?",
    message: "Could you share your availability for the next few days? I'll set up a time that works for you.",
  },
];
const DD_CANDIDATE_CHIPS = [
  {
    label: "I'm interested!",
    message: "Hi! I'm very interested in this opportunity and would love to learn more.",
  },
  {
    label: "What are the hours?",
    message: "Could you tell me more about the schedule and expected hours for this position?",
  },
  {
    label: "When can we talk?",
    message: "I'd be happy to connect. When would be a good time to talk?",
  },
];
const DDMessageInput: React.FC<{ isProfessional?: boolean }> = ({ isProfessional }) => {
  const { sendUserMessage } = useGroupChannelContext();
  const chips = isProfessional ? DD_CANDIDATE_CHIPS : DD_RECRUITER_CHIPS;
  const rootRef = React.useRef<HTMLDivElement>(null);

  const getEditable = () =>
    rootRef.current?.querySelector<HTMLElement>('.sendbird-message-input--textarea');

  // The mock keeps a permanent send button beside the field; UIKit only
  // shows its own while typing (hidden via CSS). Read the contenteditable,
  // send, then clear it and notify UIKit so its state resets.
  const handleSend = () => {
    const editable = getEditable();
    const text = (editable?.textContent || '').trim();
    if (!text) return;
    try {
      sendUserMessage({ message: text });
      if (editable) {
        editable.textContent = '';
        editable.dispatchEvent(new Event('input', { bubbles: true }));
      }
    } catch (e) { /* noop */ }
  };

  // Chips seed the field with an editable draft (caret at the end) rather
  // than sending immediately, so the recruiter can tweak before sending.
  const seedChip = (chip: string) => {
    const editable = getEditable();
    if (!editable) return;
    editable.textContent = chip;
    editable.dispatchEvent(new Event('input', { bubbles: true }));
    editable.focus();
    const selection = window.getSelection();
    if (selection) {
      const range = document.createRange();
      range.selectNodeContents(editable);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  return (
    <div className="dd-chat__input" ref={rootRef}>
      <div className="dd-chat__chips">
        {chips.map((chip) => (
          <button
            key={chip.label}
            type="button"
            className="dd-chat__chip"
            onClick={() => seedChip(chip.message)}
          >
            {chip.label}
          </button>
        ))}
      </div>
      <div className="dd-chat__composer">
        <MessageInputWrapper />
        <button type="button" className="dd-chat__send" onClick={handleSend} aria-label="Send message">
          <i className="fas fa-paper-plane" />
        </button>
      </div>
    </div>
  );
};

// ---------- DirectDental chat parts (shared by desktop + mobile) ----------
const getOtherMember = (channel: any, currentProfileUuid: string) => {
  try {
    return (channel?.members || []).find((m: any) => m && m.userId !== currentProfileUuid) || null;
  } catch (e) {
    return null;
  }
};

const ddInitials = (name?: string) =>
  (name || '?').split(' ').map((part) => part[0]).filter(Boolean).join('').toUpperCase().slice(0, 2);

const formatChannelTime = (ts?: number) => {
  if (!ts) return '';
  const date = new Date(ts);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  const days = (now.getTime() - date.getTime()) / 86400000;
  if (days < 7) return date.toLocaleDateString([], { weekday: 'short' });
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const DDAvatar: React.FC<{ member: any; name: string; size?: number }> = ({ member, name, size = 38 }) => (
  <div className="dd-chat__avatar" style={{ width: size, height: size }}>
    {member?.profileUrl ? (
      <img src={member.profileUrl} alt="" />
    ) : (
      <span>{ddInitials(name)}</span>
    )}
    {member?.connectionStatus === 'online' && <i className="dd-chat__presence" />}
  </div>
);

const DDChannelRow: React.FC<{
  channel: any;
  currentProfileUuid: string;
  active?: boolean;
  onSelect: () => void;
}> = ({ channel, currentProfileUuid, active, onSelect }) => {
  const name = getChannelDisplayName(channel, currentProfileUuid);
  const member = getOtherMember(channel, currentProfileUuid);
  const unread = channel?.unreadMessageCount || 0;
  const preview = channel?.lastMessage?.message || 'No messages yet';
  return (
    <button type="button" className={`dd-chat__row ${active ? 'dd-chat__row--active' : ''}`} onClick={onSelect}>
      <DDAvatar member={member} name={name} />
      <span className="dd-chat__row-main">
        <span className="dd-chat__row-top">
          <span className="dd-chat__row-name">{name}</span>
          <span className="dd-chat__row-time">{formatChannelTime(channel?.lastMessage?.createdAt)}</span>
        </span>
        <span className={`dd-chat__row-preview ${unread > 0 ? 'dd-chat__row-preview--unread' : ''}`}>
          {preview}
        </span>
      </span>
      {unread > 0 && <span className="dd-chat__unread">{unread > 9 ? '9+' : unread}</span>}
    </button>
  );
};

// DiDi sits at the top of the conversation list as a pinned row. It is not a
// SendBird channel, so it can't come from SBChannelList and is rendered
// above it instead.
const DDAssistantRow: React.FC<{ active?: boolean; onSelect: () => void }> = ({ active, onSelect }) => (
  <button
    type="button"
    className={`dd-chat__row dd-chat__row--assistant ${active ? 'dd-chat__row--active' : ''}`}
    data-tour="didi-row"
    onClick={onSelect}
  >
    <div className="dd-chat__avatar dd-chat__avatar--assistant" style={{ width: 38, height: 38 }}>
      <i className="fas fa-robot" />
    </div>
    <span className="dd-chat__row-main">
      <span className="dd-chat__row-top">
        <span className="dd-chat__row-name">DiDi</span>
        <span className="dd-chat__row-tag">Assistant</span>
      </span>
      <span className="dd-chat__row-preview">Ask about jobs, applicants, and candidates</span>
    </span>
  </button>
);

// Thread header for the DiDi conversation. Mirrors DDThreadHeader's markup so
// the assistant reads as a peer of the SendBird threads, with the history
// toggle in the same actions slot SendJobButton uses.
const DDAssistantHeader: React.FC<{
  onBack: (e: any) => void;
  showingHistory: boolean;
  onToggleHistory: () => void;
}> = ({ onBack, showingHistory, onToggleHistory }) => (
  <div className="dd-chat__thread-head">
    <button type="button" className="dd-chat__back" onClick={onBack} aria-label="Back to conversations">
      &#8249;
    </button>
    <div className="dd-chat__avatar dd-chat__avatar--assistant" style={{ width: 38, height: 38 }}>
      <i className="fas fa-robot" />
    </div>
    <div className="dd-chat__who">
      <div className="dd-chat__who-name">DiDi</div>
      <div className="dd-chat__who-sub">AI hiring assistant</div>
    </div>
    <div className="dd-chat__head-actions">
      <button
        type="button"
        className="dd-chat__share-btn"
        onClick={onToggleHistory}
        title={showingHistory ? 'Back to this chat' : 'Your past DiDi conversations'}
        aria-label={showingHistory ? 'Back to this chat' : 'Your past DiDi conversations'}
      >
        <i className={`fas fa-${showingHistory ? 'comment-dots' : 'history'}`} />
        {showingHistory ? 'Back' : 'History'}
      </button>
    </div>
  </div>
);

// One-line job context pinned under the thread header, per the approved
// design: "[Applied] Job title · View application". Uses the same
// endpoint as the old Job Connections accordion; "+N more" expands the
// full list inline.
const DDContextStrip: React.FC<{ otherProfileId: string; isCandidate?: boolean }> = ({ otherProfileId, isCandidate }) => {
  const [data, setData] = React.useState<any>(null);
  const [expanded, setExpanded] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    setData(null);
    setExpanded(false);
    if (!otherProfileId) return undefined;
    const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
    axios.get(`/sendbird/job_relationships/${otherProfileId}`, { headers: csrf ? { 'X-CSRF-Token': csrf } : {} })
      .then((resp) => { if (!cancelled) setData(resp.data || {}); })
      .catch(() => { if (!cancelled) setData({}); });
    return () => { cancelled = true; };
  }, [otherProfileId]);

  if (!data) return null;
  // Legacy jobs can lack a title — fall back to the job type so the strip
  // never renders an empty label.
  const relationTitle = (job: any) => job.title || job.job_type || `Job #${job.id}`;
  const relations = [
    ...(data.appliedJobs || []).map((job: any) => ({ ...job, title: relationTitle(job), tag: 'Applied', linkLabel: isCandidate ? 'View job' : 'View application' })),
    ...(data.invitedJobs || []).map((job: any) => ({ ...job, title: relationTitle(job), tag: 'Invited', linkLabel: 'View job' })),
    ...(data.tempRequests || []).map((job: any) => ({ ...job, title: relationTitle(job), tag: 'Temp', linkLabel: 'View request' })),
  ];
  if (relations.length === 0) return null;
  const primary = relations[0];
  const rest = relations.slice(1);

  return (
    <div className="dd-chat__context">
      <div className="dd-chat__context-line">
        <span className="dd-chat__context-tag">{primary.tag}</span>
        <span className="dd-chat__context-title" title={primary.title}>{primary.title}</span>
        {primary.url && (
          <a href={primary.url} target="_blank" rel="noopener noreferrer" className="dd-chat__context-link">
            {primary.linkLabel}
          </a>
        )}
        {rest.length > 0 && (
          <button type="button" className="dd-chat__context-more" onClick={() => setExpanded(!expanded)}>
            {expanded ? 'less' : `+${rest.length} more`}
          </button>
        )}
      </div>
      {expanded && rest.map((relation, index) => (
        <div className="dd-chat__context-line" key={`${relation.tag}-${relation.id || index}`}>
          <span className="dd-chat__context-tag">{relation.tag}</span>
          <span className="dd-chat__context-title" title={relation.title}>{relation.title}</span>
          {relation.url && (
            <a href={relation.url} target="_blank" rel="noopener noreferrer" className="dd-chat__context-link">
              {relation.linkLabel}
            </a>
          )}
        </div>
      ))}
    </div>
  );
};

const DDThreadHeader: React.FC<{
  channel: any;
  currentProfileUuid: string;
  otherProfileId?: string;
  onBack: (e: any) => void;
  actions?: React.ReactNode;
}> = ({ channel, currentProfileUuid, otherProfileId, onBack, actions }) => {
  const name = channel ? getChannelDisplayName(channel, currentProfileUuid) : 'Conversation';
  const member = channel ? getOtherMember(channel, currentProfileUuid) : null;
  return (
    <div className="dd-chat__thread-head">
      <button type="button" className="dd-chat__back" onClick={onBack} aria-label="Back to conversations">
        &#8249;
      </button>
      <DDAvatar member={member} name={name} />
      <div className="dd-chat__who">
        <div className="dd-chat__who-name">{name}</div>
        <div className="dd-chat__who-sub">
          {member?.connectionStatus === 'online' ? 'Online' : 'Offline'}
        </div>
      </div>
      <div className="dd-chat__head-actions">
        {otherProfileId && (
          <a
            className="dd-chat__icon-btn"
            href={`/profiles/redirect/${otherProfileId}`}
            target="_blank"
            rel="noopener noreferrer"
            title="View profile"
          >
            <i className="fas fa-user" />
          </a>
        )}
        {actions}
      </div>
    </div>
  );
};
import axios from "axios";

import {
  BrowserView,
  MobileView,
  isBrowser,
  isMobile,
} from "react-device-detect";

// Declare SendBird global types
declare global {
  interface Window {
    sendbird: any;
  }
}

// Update the renderChannelList function to use our custom handleChannelSelect
const renderChannelList = (handleChannelSelect, currentChannelUrl, profile_uuid) => {
  return (
    <SBChannelList
      disableAutoSelect
      onChannelSelect={handleChannelSelect}
      selectedChannelUrl={currentChannelUrl}
      renderChannelPreview={({ channel }) => (
          <DDChannelRow
            channel={channel}
            currentProfileUuid={profile_uuid}
            active={channel?.url === currentChannelUrl}
            onSelect={() => channel && handleChannelSelect(channel)}
          />
        )}
      disableUserProfile={true}
      onChannelCreated={() => {}}
      onUserProfileMessage={() => {}}
      onStartDirectMessage={() => {}}
      renderUserProfile={() => null}
    />
  );
};

// Define the URL cleaning utility at the component level for reuse
const cleanProfileUrl = (url) => {
  if (!url) return '/images/missing.svg';
  
  // Fix URLs with https//
  if (url.startsWith('https//') || url.startsWith('http//')) {
    return url.replace(/^(https?)\/{2}/, '$1://');
  }
  
  // Fix URLs with double protocol (https://https://)
  if (url.match(/^https?:\/\/https?:\/\//)) {
    return url.replace(/^https?:\/\//, '');
  }
  
  // Fix URLs with double protocol (https:https://)
  if (url.match(/^https?:https?:\/\//)) {
    return url.replace(/^https?:/, '');
  }
  
  return url;
};

// Get a readable channel display name excluding the current user's name
const getChannelDisplayName = (channel, currentProfileUuid) => {
  try {
    if (channel?.members && Array.isArray(channel.members)) {
      const otherNames = channel.members
        .filter((m) => m && m.userId !== currentProfileUuid)
        .map((m) => m.nickname || m.name || m.userId)
        .filter(Boolean);

      if (otherNames.length > 0) {
        return otherNames.join(', ');
      }
    }

    // Fallback to channel.name or a generic label
    return channel?.name || 'Chat';
  } catch (e) {
    return channel?.name || 'Chat';
  }
};

export default function SBChatModal({
  avatar_url,
  nickname,
  profile_uuid,
  profile_id,
  app_id,
  initialChannelUrl,
  isProfessional,
  assistantEnabled = false,
  userName = "",
}) {
  const currentChannelUrl = useStore(SBChatChannel);
  const show = useStore(SBChatShow);
  const unreadCount = useStore(SBChatUnread);
  // The assistant thread wins over any selected channel, so opening DiDi
  // doesn't have to tear down the SendBird selection to render.
  const assistantOpenRaw = useStore(SBChatAssistant);
  const assistantOpen = assistantEnabled && assistantOpenRaw;
  // Which assistant panel shows is the store's own activePanel, not local
  // state: loadConversation and startNewConversation already flip it back to
  // 'chat', so picking a conversation returns to the thread even when it is
  // the one already open.
  const assistantHistory = useStore(assistantWidget).activePanel === 'conversations';

  const openAssistant = React.useCallback(() => {
    SBChatAssistant.set(true);
    assistantWidget.setKey('activePanel', 'chat');
  }, []);

  const closeAssistant = React.useCallback((e?: any) => {
    if (e && e.preventDefault) e.preventDefault();
    SBChatAssistant.set(false);
    assistantWidget.setKey('activePanel', 'chat');
  }, []);

  const toggleAssistantHistory = React.useCallback(() => {
    assistantWidget.setKey('activePanel', assistantHistory ? 'chat' : 'conversations');
  }, [assistantHistory]);

  const renderAssistantThread = () => (
    <>
      <AssistantStyles />
      <DDAssistantHeader
        onBack={closeAssistant}
        showingHistory={assistantHistory}
        onToggleHistory={toggleAssistantHistory}
      />
      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {assistantHistory ? <AssistantConversationList /> : <AssistantChat userName={userName} />}
      </div>
    </>
  );
  // Window geometry persists across sessions; saved values are clamped
  // back into the current viewport so the panel can never come back
  // offscreen (for example after a monitor change).
  const savedWindow = useRef<{ x?: number; y?: number; width?: number; height?: number } | null>(
    (() => {
      try {
        return JSON.parse(window.localStorage.getItem("dd-chat-window") || "null");
      } catch (e) {
        return null;
      }
    })(),
  );
  const [size, setSize] = useState(() => ({
    width: Math.max(340, Math.min(savedWindow.current?.width || 420, window.innerWidth - 16)),
    height: Math.max(420, Math.min(savedWindow.current?.height || 640, window.innerHeight - 16)),
  }));
  const [channelListWidth, setChannelListWidth] = useState(180);
  const [isDragging, setIsDragging] = useState(false);
  // Default bottom-right with a margin, clamped fully on screen.
  const [position, setPosition] = useState(() => {
    const w = Math.max(340, Math.min(savedWindow.current?.width || 420, window.innerWidth - 16));
    const h = Math.max(420, Math.min(savedWindow.current?.height || 640, window.innerHeight - 16));
    const x = savedWindow.current?.x ?? window.innerWidth - w - 24;
    const y = savedWindow.current?.y ?? window.innerHeight - h - 24;
    return {
      x: Math.min(Math.max(8, x), Math.max(8, window.innerWidth - w - 8)),
      y: Math.min(Math.max(8, y), Math.max(8, window.innerHeight - h - 8)),
    };
  });
  // Small viewports get a full-screen panel instead of a floating one.
  const [isCompactViewport, setIsCompactViewport] = useState(
    window.innerWidth < 640 || window.innerHeight < 600,
  );
  const dragRef = useRef(null);
  const [mobileView, setMobileViewState] = useState(
    currentChannelUrl ? "conversation" : "channels",
  );
  const sendbirdRef = useRef(null);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  // Add loading state to prevent multiple channel initializations
  const [isLoadingChannel, setIsLoadingChannel] = useState(false);
  // The channel object behind currentChannelUrl, for the thread header
  // (name, avatar, presence). Set on selection; null on deep links.
  const [selectedChannel, setSelectedChannel] = useState<any>(null);
  // Conversation-list search (filters channels by the other member's nickname)
  const [listSearch, setListSearch] = useState('');
  // Add a ref to track last processed channel to prevent duplicate processing
  const lastProcessedChannelRef = useRef<string | null>(null);

  // Flag to prevent view changes during back button operation
  const isBackButtonOperation = useRef(false);

  // Get CSRF token for API requests
  useEffect(() => {
    const token = document.querySelector<HTMLMetaElement>(
      'meta[name="csrf-token"]'
    )?.content;
    setCsrfToken(token || null);
  }, []);

  // Function to mark messages as read
  const markMessagesAsRead = async () => {
    if (!csrfToken) {
      // CSRF token missing; cannot mark messages as read
      return;
    }
    
    try {
      await axios.post('/sendbird/mark_as_read', {}, {
        headers: {
          'X-CSRF-Token': csrfToken
        }
      });
      
      // Update local store immediately
      SBChatUnread.set(0);
    } catch (error) {
      console.error('[SBChatModal] Failed to mark messages as read from modal:', error);
    }
  };

  // Clean wrapper for the state setter
  const setMobileView = (view) => {
    setMobileViewState(view);
  };

  const setShow = () => {
    if (isMobile) {
      switchToChannelList();
    }
    SBChatShow.set(!show);
  };

  // Function to handle transition to channel list view
  const switchToChannelList = () => {
    isBackButtonOperation.current = true;
    
    try {
      // Clear the reference to the last channel to allow a clean switch
      lastProcessedChannelRef.current = null;
      setMobileViewState("channels");
      setSelectedChannel(null);
      setIsLoadingChannel(false);
      SBChatChannel.set(null);
      
      // Ensure state is fully reset
      setTimeout(() => {
        lastProcessedChannelRef.current = null;
        setMobileViewState("channels");
        
        // Reset flag after everything is done
        setTimeout(() => {
          isBackButtonOperation.current = false;
        }, 300);
      }, 100);
    } catch (error) {
      console.error("[SBChatModal] Error switching to channel list:", error);
      isBackButtonOperation.current = false;
    }
  };

  const handleMobileBack = (e) => {
    e.preventDefault();
    e.stopPropagation();
    switchToChannelList();
  };

  // Add debounced channel processing to prevent race conditions
  useEffect(() => {
    if (!show || !currentChannelUrl || isLoadingChannel || 
        currentChannelUrl === lastProcessedChannelRef.current) {
      return;
    }

    lastProcessedChannelRef.current = currentChannelUrl;
    setIsLoadingChannel(true);
    
    const timer = setTimeout(() => {
      try {
        if (isMobile) {
          setMobileView("conversation");
        }
        setIsLoadingChannel(false);
      } catch (error) {
        console.error("[SBChatModal] Error initializing channel:", error);
        setIsLoadingChannel(false);
      }
    }, 50);
    
    return () => clearTimeout(timer);
  }, [currentChannelUrl, show]);

  // Handle view updates based on channel changes
  useEffect(() => {
    if (currentChannelUrl && isMobile && !isBackButtonOperation.current) {
      setMobileView("conversation");
    }
  }, [currentChannelUrl]);

  // Monitor and cleanup channel changes
  useEffect(() => {
    if (!currentChannelUrl && isBackButtonOperation.current) {
      setMobileViewState("channels");
      
      const checkInterval = setInterval(() => {
        const currentChannelUrlNow = SBChatChannel.get();
        if (currentChannelUrlNow) {
          SBChatChannel.set(null);
        }
      }, 50);
      
      setTimeout(() => clearInterval(checkInterval), 500);
    }
  }, [currentChannelUrl]);
  
  // Ensure flags are properly reset
  useEffect(() => {
    if (show && mobileView === "channels") {
      const timer = setTimeout(() => {
        if (isBackButtonOperation.current && mobileView === "channels") {
          isBackButtonOperation.current = false;
        }
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [show, mobileView]);

  // Handle hardware back button for mobile
  useEffect(() => {
    if (isMobile && show) {
      const handleBackButton = (e) => {
        if (mobileView === "conversation" && currentChannelUrl) {
          e.preventDefault();
          setMobileView("channels");
          SBChatChannel.set(null);
          return false;
        }
        return true;
      };
      
      window.addEventListener('popstate', handleBackButton);
      return () => window.removeEventListener('popstate', handleBackButton);
    }
  }, [show, mobileView, currentChannelUrl, isMobile]);

  // Function to update the total unread message count from all channels
  const updateTotalUnreadCount = async () => {
    try {
      // Get global SendBird instance
      if (window.sendbird && window.sendbird.GroupChannel) {
        const listQuery = window.sendbird.GroupChannel.createMyGroupChannelListQuery();
        listQuery.includeUnreadCount = true;
        listQuery.limit = 100;
        
        const channels = await listQuery.next();
        let totalUnread = 0;
        
        if (channels && channels.length > 0) {
          channels.forEach(channel => {
            totalUnread += channel.unreadMessageCount || 0;
          });
        }
        
        // Update the global unread count
        SBChatUnread.set(totalUnread);
        } else {
        // SendBird SDK not available in this runtime
      }
    } catch (error) {
      console.error("[SBChatModal] Error updating total unread count:", error);
    }
  };

  // When a channel is selected, mark messages as read
  const handleChannelSelect = (channel) => {
    if (!channel || !channel.url || isLoadingChannel) {
      return;
    }
    // UIKit's list auto-select fires during the back transition; honoring
    // it would silently reopen the conversation the user just left.
    if (isBackButtonOperation.current) {
      return;
    }
    
    try {
      // Reset any lingering flags from previous back operations
      isBackButtonOperation.current = false;
      
      SBChatChannel.set(channel.url);
      setSelectedChannel(channel);
      markMessagesAsRead();
      
      if (isMobile) {
        setMobileView("conversation");
      }
    } catch (error) {
      console.error("[SBChatModal] Error selecting channel:", error);
    }
  };

  const onResize = (event, { size: next }) => {
    // Clamp so growing the panel can never push it past the viewport edge
    setSize({
      width: Math.max(340, Math.min(next.width, window.innerWidth - position.x - 8)),
      height: Math.max(420, Math.min(next.height, window.innerHeight - position.y - 8)),
    });
  };

  const onChannelListResize = (event, { size }) => {
    setChannelListWidth(size.width);
  };

  const onDragStart = (e) => {
    setIsDragging(true);
    const startX = e.clientX - position.x;
    const startY = e.clientY - position.y;

    const onDrag = (e) => {
      setPosition({
        x: Math.min(Math.max(8, e.clientX - startX), Math.max(8, window.innerWidth - size.width - 8)),
        y: Math.min(Math.max(8, e.clientY - startY), Math.max(8, window.innerHeight - size.height - 8)),
      });
    };

    const onDragEnd = () => {
      setIsDragging(false);
      document.removeEventListener("mousemove", onDrag);
      document.removeEventListener("mouseup", onDragEnd);
    };

    document.addEventListener("mousemove", onDrag);
    document.addEventListener("mouseup", onDragEnd);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsCompactViewport(window.innerWidth < 640 || window.innerHeight < 600);
      setPosition((prev) => ({
        x: Math.min(Math.max(8, prev.x), Math.max(8, window.innerWidth - size.width - 8)),
        y: Math.min(Math.max(8, prev.y), Math.max(8, window.innerHeight - size.height - 8)),
      }));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [size]);

  // Remember the window geometry across sessions (floating mode only)
  useEffect(() => {
    if (isCompactViewport) return;
    try {
      window.localStorage.setItem(
        "dd-chat-window",
        JSON.stringify({ x: position.x, y: position.y, width: size.width, height: size.height }),
      );
    } catch (e) { /* storage unavailable; ignore */ }
  }, [position, size, isCompactViewport]);

  // Always land on the newest message when a conversation opens. Job cards
  // and images finish rendering after mount and shift the layout, so nudge
  // the scroller a few times instead of once.
  useEffect(() => {
    if (!show || !currentChannelUrl) return;
    const scrollToNewest = () => {
      const scroller = document.querySelector<HTMLElement>(".sendbird-conversation__messages-padding");
      if (scroller) scroller.scrollTop = scroller.scrollHeight;
    };
    const timers = [150, 450, 900, 1600, 2600].map((ms) => window.setTimeout(scrollToNewest, ms));
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [show, currentChannelUrl]);

  // Seed the initial channel exactly once — re-running this after the user
  // backs out to the list silently reopened the conversation, which made
  // the back button appear broken.
  const initialChannelSeededRef = useRef(false);
  useEffect(() => {
    if (initialChannelUrl && !initialChannelSeededRef.current) {
      if (typeof initialChannelUrl === 'string' && initialChannelUrl.trim() !== '') {
        initialChannelSeededRef.current = true;
        SBChatChannel.set(initialChannelUrl);
      } else {
        console.error("Invalid initialChannelUrl provided:", initialChannelUrl);
      }
    }
  }, [initialChannelUrl]);

  // Setup message received handler
  useEffect(() => {
    // When the chat is shown, update the total unread count
    if (show) {
      updateTotalUnreadCount();
      
      // Setup global message event handler to listen for new messages
      const handleChannelChanged = () => {
        updateTotalUnreadCount();
      };
      
      // Try to get SendBird SDK instance
      if (window.sendbird) {
        window.sendbird.addChannelHandler('global_unread_handler', {
          onMessageReceived: handleChannelChanged,
          onUnreadMemberStatusUpdated: handleChannelChanged,
          onUnreadItemCountUpdated: handleChannelChanged,
          onChannelChanged: handleChannelChanged
        });
      }
      
      return () => {
        // Remove event handler when component unmounts or chat is hidden
        if (window.sendbird) {
          window.sendbird.removeChannelHandler('global_unread_handler');
        }
      };
    }
  }, [show]);

  // Ensure clean state for channel selection
  const clearSelectionState = () => {
    isBackButtonOperation.current = false;
    setIsLoadingChannel(false);
  };

  if (!show) {
    return null;
  }

  // Renders server-sent job_invite / temp_request messages as a JobWidget card,
  // everything else as the default UIKit message. Shared by the desktop and
  // mobile threads — mobile previously omitted it entirely, so a shared job
  // showed up as a plain text message with no card on phones.
  const renderJobAwareMessage = ({ message, ...rest }: any) => {
    try {
      const customType = (message && (message.customType || message.custom_type)) || '';
      if (customType === 'job_invite' || customType === 'temp_request') {
        const dataStr = (message && (message.data || message._data)) || null;
        let parsed = null;
        try {
          parsed = typeof dataStr === 'string' ? JSON.parse(dataStr) : dataStr;
        } catch (e) {
          console.warn('[SBChatModal] Failed to parse message.data for JobWidget', e);
          // fall through to default message rendering
        }

        if (parsed) {
          const JobWidget = require('./JobWidget').default;
          return <div style={{ padding: 8 }}><JobWidget data={parsed} isProfessional={isProfessional} /></div>;
        }
      }
    } catch (e) {
      console.error('[SBChatModal] Error in renderMessageContent:', e);
    }

    // Render default message for any non-job messages or parse failures
    return <Message {...(rest as any)} message={message as any} />;
  };

  // Rendering helper for conversation view
  const renderConversation = () => {
    if (!currentChannelUrl) {
      return (
        <div style={{ 
          height: "100%", 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center",
          padding: "20px",
          color: "#666" 
        }}>
          No chat selected or invalid channel
        </div>
      );
    }
    
    // Extract other profile ID from the channel URL
    const channelUsers = currentChannelUrl.split('_');
    const otherProfileId = channelUsers.find(user => user !== profile_uuid && user.trim() !== '') || '';
    
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <DDThreadHeader
          channel={selectedChannel}
          currentProfileUuid={profile_uuid}
          otherProfileId={otherProfileId}
          onBack={(e) => { e.preventDefault(); switchToChannelList(); }}
          actions={!isProfessional && otherProfileId ? (() => {
            const SendJobButton = require('./SendJobButton').default;
            return (
              <SendJobButton
                channelUrl={currentChannelUrl}
                otherProfileId={otherProfileId}
                authenticityToken={csrfToken}
              />
            );
          })() : null}
        />
        {/* Job relationship pinned as a context strip above the thread */}
        {otherProfileId && (
          <DDContextStrip otherProfileId={otherProfileId} isCandidate={isProfessional} />
        )}
        <div style={{ flex: 1, overflow: "hidden" }}>
          <SBConversation
            channelUrl={currentChannelUrl}
            renderChannelHeader={() => null}
            renderMessageInput={() => <DDMessageInput isProfessional={isProfessional} />}
            renderMessageContent={renderJobAwareMessage}
            renderUserProfile={(props) => {
              if (props.user?.userId) {
                window.open(
                  `/profiles/redirect/${props.user.userId}`,
                  "_blank",
                );
              }
              return null;
            }}
            onBeforeSendUserMessage={() => {
              // Will be called before sending a message
              // We'll use this to trigger our update after sending
              setTimeout(updateTotalUnreadCount, 1000);
              return;
            }}
            onUserProfileMessage={() => {}}
            onStartDirectMessage={() => {}}
            disableUserProfile={true}
          />
        </div>
        
      </div>
    );
  };

  // Update the renderChannelList function to use our custom handleChannelSelect
  const renderChannelList = () => {
    return (
      <SBChannelList
        disableAutoSelect
        channelListQueryParams={listSearch.trim() ? { nicknameContainsFilter: listSearch.trim() } : undefined}
        onChannelSelect={handleChannelSelect}
        selectedChannelUrl={currentChannelUrl}
        renderHeader={() => {
          // Clear selection state when rendering header
          clearSelectionState();
          return (
            <div
              style={{
                padding: "12px",
                display: "flex",
                alignItems: "center",
                borderBottom: "1px solid #e0e0e0",
                backgroundColor: "#f8f9fa",
                width: "100%",
              }}
            >
              <img
                src={cleanProfileUrl(avatar_url)}
                alt="User avatar"
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  marginRight: "8px",
                  objectFit: "cover",
                }}
                onError={(e) => { e.currentTarget.src = '/images/missing.svg' }}
              />
              {isProfessional ? (
                <a
                  href={`/profiles/${profile_id}`}
                  style={{
                    textDecoration: "none",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#085ec8",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {nickname}
                  </span>
                  <span
                    style={{
                      fontSize: "9px",
                      color: "#666",
                    }}
                  >
                    View Profile
                  </span>
                </a>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#212529",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {nickname}
                  </span>
                </div>
              )}
            </div>
          );
        }}
        renderChannelPreview={({ channel }) => (
          <DDChannelRow
            channel={channel}
            currentProfileUuid={profile_uuid}
            active={channel?.url === currentChannelUrl}
            onSelect={() => channel && handleChannelSelect(channel)}
          />
        )}
        disableUserProfile={true}
        onChannelCreated={() => {}}
        onUserProfileMessage={() => {}}
        onStartDirectMessage={() => {}}
        renderUserProfile={() => null}
      />
    );
  };

  const modalContent = (
    <>
      <BrowserView>
        <Resizable
          width={size.width}
          height={size.height}
          onResize={onResize}
          minConstraints={[350, 400]}
          maxConstraints={[1000, 800]}
          handle={<div className="dd-chat__resize-handle" style={isCompactViewport ? { display: "none" } : undefined} />}
        >
          <div
            style={isCompactViewport ? {
              position: "fixed",
              inset: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "white",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              zIndex: 9998,
            } : {
              position: "fixed",
              width: `${size.width}px`,
              height: `${size.height}px`,
              bottom: `${window.innerHeight - position.y - size.height}px`,
              right: `${window.innerWidth - position.x - size.width}px`,
              backgroundColor: "white",
              boxShadow: "0 12px 40px rgba(15, 35, 70, 0.18)",
              border: "1px solid #e3e9f1",
              borderRadius: "16px",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              zIndex: 9998,
            }}
          >
            <div ref={dragRef} onMouseDown={onDragStart} className="dd-chat__titlebar">
              <h3 className="dd-chat__title">Messages</h3>
              <button onClick={() => setShow()} className="dd-chat__close" aria-label="Close messages">
                &times;
              </button>
            </div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              {assistantOpen ? (
                renderAssistantThread()
              ) : currentChannelUrl ? (
                renderConversation()
              ) : (
                <div className="dd-chat__list-wrap">
                  <div className="dd-chat__list-search">
                    <input
                      className="dd-chat__sheet-input"
                      placeholder="Search conversations…"
                      value={listSearch}
                      onChange={(e) => setListSearch(e.target.value)}
                      aria-label="Search conversations"
                    />
                  </div>
                  {assistantEnabled && !listSearch.trim() && (
                    <div className="dd-chat__pinned">
                      <DDAssistantRow onSelect={openAssistant} />
                    </div>
                  )}
                  <div className="dd-chat__list">
                    {renderChannelList()}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Resizable>
      </BrowserView>
      <MobileView>
        <div
          key={`mobile-view-${mobileView}`}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "white",
            zIndex: 9998,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div className="dd-chat__titlebar dd-chat__titlebar--mobile">
            {assistantOpen ? (
              <button
                onClick={closeAssistant}
                style={{
                  background: "none",
                  border: "none",
                  color: "white",
                  marginRight: "8px",
                  fontSize: "20px",
                  cursor: "pointer",
                  padding: "5px 10px",
                  display: "flex",
                  alignItems: "center",
                  fontWeight: "bold",
                }}
              >
                <span style={{ marginRight: "5px" }}>&larr;</span>
                <span style={{ fontSize: "16px" }}>Back</span>
              </button>
            ) : mobileView === "conversation" && currentChannelUrl ? (
              <button
                onClick={handleMobileBack}
                style={{
                  background: "none",
                  border: "none",
                  color: "white",
                  marginRight: "8px",
                  fontSize: "20px",
                  cursor: "pointer",
                  padding: "5px 10px",
                  display: "flex",
                  alignItems: "center",
                  fontWeight: "bold",
                }}
              >
                <span style={{ marginRight: "5px" }}>&larr;</span>
                <span style={{ fontSize: "16px" }}>Back</span>
              </button>
            ) : null}
            <h3 style={{ margin: 0 }}>
              {assistantOpen ? "DiDi" : mobileView === "channels" ? "Messages" : "Conversation"}
            </h3>
            <button
              onClick={() => setShow()}
              style={{
                background: "none",
                border: "none",
                color: "white",
                fontSize: "20px",
                cursor: "pointer",
                padding: 0,
              }}
            >
              &times;
            </button>
          </div>
          <div style={{ flex: 1, overflow: "hidden" }}>
            {assistantOpen ? (
              <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <AssistantStyles />
                {assistantHistory ? <AssistantConversationList /> : <AssistantChat userName={userName} />}
              </div>
            ) : mobileView === "channels" ? (
              <div style={{ height: "100%", overflow: "auto" }}>
                {assistantEnabled && (
                  <div className="dd-chat__pinned">
                    <DDAssistantRow onSelect={openAssistant} />
                  </div>
                )}
                <SBChannelList
                  disableAutoSelect
                  channelListQueryParams={listSearch.trim() ? { nicknameContainsFilter: listSearch.trim() } : undefined}
                  onChannelSelect={handleChannelSelect}
                  selectedChannelUrl={currentChannelUrl}
                  renderHeader={() => {
                    // Clear selection state when rendering header
                    clearSelectionState();
                    return (
                      <div
                        style={{
                          padding: "16px",
                          display: "flex",
                          alignItems: "center",
                          borderBottom: "1px solid #e0e0e0",
                          backgroundColor: "#f8f9fa",
                          width: "100%",
                        }}
                      >
                        <img
                          src={cleanProfileUrl(avatar_url)}
                          alt="User avatar"
                          style={{
                            width: "36px",
                            height: "36px",
                            borderRadius: "50%",
                            marginRight: "12px",
                            objectFit: "cover",
                          }}
                          onError={(e) => { e.currentTarget.src = '/images/missing.svg' }}
                        />
                        {isProfessional ? (
                          <a
                            href={`/profiles/${profile_id}`}
                            style={{
                              textDecoration: "none",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              overflow: "hidden",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "16px",
                                fontWeight: "600",
                                color: "#085ec8",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {nickname}
                            </span>
                            <span
                              style={{
                                fontSize: "12px",
                                color: "#666",
                                marginTop: "2px",
                              }}
                            >
                              View Profile
                            </span>
                          </a>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              overflow: "hidden",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "16px",
                                fontWeight: "600",
                                color: "#212529",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {nickname}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  }}
                  renderChannelPreview={({ channel }) => (
          <DDChannelRow
            channel={channel}
            currentProfileUuid={profile_uuid}
            active={channel?.url === currentChannelUrl}
            onSelect={() => channel && handleChannelSelect(channel)}
          />
        )}
                  disableUserProfile={true}
                  onChannelCreated={() => {}}
                  onUserProfileMessage={() => {}}
                  onStartDirectMessage={() => {}}
                  renderUserProfile={() => null}
                />
              </div>
            ) : mobileView === "conversation" && currentChannelUrl ? (
              <div style={{ height: "100%" }}>
                {(() => {
                  const channelUsers = currentChannelUrl.split('_');
                  const mobileHeaderProfileId = channelUsers.find(user => user !== profile_uuid && user.trim() !== '') || '';
                  return (
                    <DDThreadHeader
                      channel={selectedChannel}
                      currentProfileUuid={profile_uuid}
                      otherProfileId={mobileHeaderProfileId}
                      onBack={handleMobileBack}
                      actions={!isProfessional && mobileHeaderProfileId ? (() => {
                        const SendJobButton = require('./SendJobButton').default;
                        return (
                          <SendJobButton
                            channelUrl={currentChannelUrl}
                            otherProfileId={mobileHeaderProfileId}
                            authenticityToken={csrfToken}
                          />
                        );
                      })() : null}
                    />
                  );
                })()}

                {/* Job relationship pinned as a context strip above the thread */}
                {(() => {
                  const channelUsers = currentChannelUrl.split('_');
                  const mobileOtherProfileId = channelUsers.find(user => user !== profile_uuid && user.trim() !== '') || '';
                  return mobileOtherProfileId ? (
                    <DDContextStrip otherProfileId={mobileOtherProfileId} isCandidate={isProfessional} />
                  ) : null;
                })()}

                <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                  <div style={{ flex: 1, overflow: "hidden" }}>
                    <SBConversation
                      channelUrl={currentChannelUrl}
                      renderChannelHeader={() => null}
                      renderMessageInput={() => <DDMessageInput isProfessional={isProfessional} />}
                      renderMessageContent={renderJobAwareMessage}
                      renderUserProfile={(props) => {
                        if (props.user?.userId) {
                          window.open(
                            `/profiles/redirect/${props.user.userId}`,
                            "_blank",
                          );
                        }
                        return null;
                      }}
                      onBeforeSendUserMessage={() => {
                        // Will be called before sending a message
                        // We'll use this to trigger our update after sending
                        setTimeout(updateTotalUnreadCount, 1000);
                        return;
                      }}
                      onUserProfileMessage={() => {}}
                      onStartDirectMessage={() => {}}
                      disableUserProfile={true}
                    />
                  </div>
                </div>

              </div>
            ) : (
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "20px",
                  textAlign: "center",
                  color: "#999",
                }}
              >
                Select a conversation from the list or start a new chat
              </div>
            )}
          </div>
          <style>
            {`
              body {
                overflow: hidden;
              }
            `}
          </style>
        </div>
      </MobileView>
    </>
  );

  // Render modal using portal to escape nav container
  return createPortal(modalContent, document.body);
}